/* TOADS refine bar
 *
 * Three jobs, none of which the markup can do on its own:
 *   1. Dual-handle price slider — clamp the two thumbs so min never crosses
 *      max, paint the fill between them, and format the readout in rupees.
 *   2. Auto-submit — price on release, sort on change. The Apply button stays
 *      in the DOM and is only hidden once this file runs, so no-JS still works.
 *   3. Drawer plumbing — close on scrim/Close/Escape, and don't let the page
 *      behind it scroll.
 *
 * Deliberately no framework and no fetch: submitting a real GET means the URL
 * always describes the current view, so back/forward, refresh and sharing all
 * behave. Filtering by URL is the whole reason the tag-path approach works.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- price */

  function initPrice(form) {
    var lo = form.querySelector('.toads-price__range--lo');
    var hi = form.querySelector('.toads-price__range--hi');
    var track = form.querySelector('[data-toads-track]');
    var out = form.querySelector('[data-toads-price-out]');
    if (!lo || !hi || !track) return;

    var floor = Number(form.dataset.floor || 0);
    var ceiling = Number(form.dataset.ceiling || 0);
    var symbol = form.dataset.symbol || '₹';
    if (!(ceiling > floor)) return;

    // The form's action already carries the tag path (Liquid puts it there so
    // no-JS works too), so there is nothing to rewrite here.
    form.dataset.js = 'true';

    var step = Number(lo.step || 1);

    function money(n) {
      // Indian grouping: 1,79,900 not 179,900. Intl handles it; the fallback
      // matters because some older Android WebViews lack the en-IN data.
      try {
        return symbol + new Intl.NumberFormat('en-IN', {
          maximumFractionDigits: 0
        }).format(n);
      } catch (e) {
        return symbol + String(n);
      }
    }

    function pct(v) {
      return ((v - floor) / (ceiling - floor)) * 100;
    }

    function paint() {
      var a = Number(lo.value);
      var b = Number(hi.value);
      track.style.setProperty('--lo', pct(a) + '%');
      track.style.setProperty('--hi', pct(b) + '%');
      if (out) {
        out.textContent = money(a) + ' – ' + money(b);
      }
      // The whole range selected is the same as no filter. Clearing the
      // inputs' names keeps those params out of the URL entirely, so the
      // "Clear all" count and the address bar stay honest.
      lo.disabled = false;
      hi.disabled = false;
    }

    function clamp(moved) {
      var a = Number(lo.value);
      var b = Number(hi.value);
      if (a > b - step) {
        if (moved === lo) {
          lo.value = Math.max(floor, b - step);
        } else {
          hi.value = Math.min(ceiling, a + step);
        }
      }
      paint();
    }

    lo.addEventListener('input', function () { clamp(lo); });
    hi.addEventListener('input', function () { clamp(hi); });

    // Submit on release, not on every input event — dragging a slider should
    // not fire a page load per pixel.
    var submitting = false;
    function apply() {
      if (submitting) return;
      var a = Number(lo.value);
      var b = Number(hi.value);
      // A full-range selection means "no price filter". Drop the params.
      if (a <= floor) lo.removeAttribute('name');
      if (b >= ceiling) hi.removeAttribute('name');
      submitting = true;
      form.submit();
    }

    ['change', 'pointerup', 'touchend', 'keyup'].forEach(function (evt) {
      lo.addEventListener(evt, function (e) {
        if (evt === 'keyup' && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(e.key) === -1) return;
        apply();
      });
      hi.addEventListener(evt, function (e) {
        if (evt === 'keyup' && ['ArrowLeft', 'ArrowRight', 'Home', 'End'].indexOf(e.key) === -1) return;
        apply();
      });
    });

    // The track's end labels get the same Indian grouping as the readout.
    var endLo = form.querySelector('[data-toads-end-lo]');
    var endHi = form.querySelector('[data-toads-end-hi]');
    if (endLo) endLo.textContent = money(floor);
    if (endHi) endHi.textContent = money(ceiling);

    paint();
  }

  /* ----------------------------------------------------------------- sort */

  function initSort(form) {
    var select = form.querySelector('select[name="sort_by"]');
    if (!select) return;
    select.addEventListener('change', function () { form.submit(); });
  }

  /* --------------------------------------------------------------- drawer */

  function initDrawer(details) {
    var wrapper = document.querySelector('.page-wrapper') || document.documentElement;

    function close() {
      details.open = false;
    }

    details.addEventListener('click', function (e) {
      var closer = e.target.closest('[data-toads-close]');
      if (closer) {
        e.preventDefault();
        close();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && details.open) close();
    });

    // :has() covers the scroll lock in CSS, but Safari 15 and some Android
    // WebViews don't have it, so mirror it with a class.
    new MutationObserver(function () {
      wrapper.classList.toggle('toads-noscroll', details.open);
      if (!details.open) return;

      var first = details.querySelector('.toads-fdrawer__close');
      if (first) first.focus({ preventScroll: true });

      /* Failsafe. The open animation is a keyframe with `both` fill, so if the
         browser throttles animations — a backgrounded tab, a hidden webview —
         the panel can sit parked at its from-state, i.e. fully off screen. A
         filter drawer that opens to nothing is worse than one that appears
         without a slide, so once the animation's own duration has passed we
         pin the resting state ourselves. */
      var panel = details.querySelector('.toads-fdrawer__panel');
      if (!panel) return;
      setTimeout(function () {
        if (!details.open) return;
        var t = getComputedStyle(panel).transform;
        if (t && t !== 'none' && t !== 'matrix(1, 0, 0, 1, 0, 0)') {
          panel.style.animation = 'none';
          panel.style.transform = 'none';
        }
      }, 700);
    }).observe(details, { attributes: true, attributeFilter: ['open'] });

    // Reset the inline override so a reopen animates again.
    details.addEventListener('toggle', function () {
      if (details.open) return;
      var panel = details.querySelector('.toads-fdrawer__panel');
      if (panel) { panel.style.animation = ''; panel.style.transform = ''; }
    });
  }

  /* ------------------------------------------------------------------ go */

  function boot() {
    document.querySelectorAll('[data-toads-price]').forEach(initPrice);
    document.querySelectorAll('[data-toads-sort]').forEach(initSort);
    document.querySelectorAll('.toads-fdrawer').forEach(initDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Horizon swaps section HTML on filter/paginate without a full reload.
  document.addEventListener('shopify:section:load', boot);
})();
